import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit';
import {message} from 'antd';
import {AxiosError, AxiosResponse} from 'axios';
import type FILTERS from '../../../../common/filters.type';
import {ADD_RACK, RACK_DATA, UPDATE_RACK} from './racks.type';
import racksServices from './racksServices';

const initialState = {
  isLoading: false,
  racks: {
    page_size: 10,
    page_number: 1,
    sort_by: 'name',
    ascending: true,
    search: '',
    is_active: undefined as undefined | boolean,
    totalDataCount: 0,
    pageData: [] as RACK_DATA[],
  },
};

export const racksList = createAsyncThunk(
  'rackList',
  async (payload: {station_id?: number[]; filters?: FILTERS}, thunkAPI) => {
    try {
      const data: AxiosResponse = await racksServices.racksList(payload);

      return data;
    } catch (error) {
      const result = error as AxiosError;

      return thunkAPI.rejectWithValue(result.message);
    }
  },
);

export const addRack = createAsyncThunk('addRack', async (values: ADD_RACK, thunkAPI) => {
  try {
    const data: AxiosResponse = await racksServices.addRack(values);

    return data;
  } catch (error) {
    const result = error as AxiosError;

    return thunkAPI.rejectWithValue(result.message);
  }
});
export const updateRack = createAsyncThunk(
  'updateRack',
  async (
    values: {
      payload: UPDATE_RACK;
      id: number;
    },
    thunkAPI,
  ) => {
    try {
      const data: AxiosResponse = await racksServices.updateRack({
        payload: values.payload,
        id: values.id,
      });

      return data;
    } catch (error) {
      const result = error as AxiosError;

      return thunkAPI.rejectWithValue(result.message);
    }
  },
);

export const racksSlice = createSlice({
  name: 'racks',
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    pageNumberChange(state, action: PayloadAction<number>) {
      state.racks.page_number = action.payload;
    },
    pageSizeChange(state, action: PayloadAction<number>) {
      state.racks.page_number = 1;
      state.racks.page_size = action.payload;
    },
    searchBy(state, action: PayloadAction<string>) {
      state.racks.page_number = 1;
      state.racks.search = action.payload;
    },
    sortBy(state, action: PayloadAction<{ascending: boolean; sortBy: string}>) {
      state.racks.ascending = action.payload.ascending;
      state.racks.sort_by = action.payload.sortBy;
    },
    racksStatusFilter(state, action: PayloadAction<boolean | undefined>) {
      state.racks.page_number = 1;
      state.racks.is_active = action.payload;
    },
    clearRacksFilters(state) {
      state.racks.page_size = 10;
      state.racks.page_number = 1;
      state.racks.search = '';
      state.racks.sort_by = 'name';
      state.racks.is_active = undefined;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(racksList.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(racksList.fulfilled, (state, action) => {
      state.isLoading = false;
      state.racks = action.payload.data;
    });
    builder.addCase(racksList.rejected, (state, action) => {
      state.isLoading = false;
      state.racks = initialState.racks;
      message.error(`${action.payload}`);
    });

    builder.addCase(addRack.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(addRack.fulfilled, (state) => {
      state.isLoading = false;
      message.success(`Rack added Successfully`);
    });
    builder.addCase(addRack.rejected, (state, action) => {
      state.isLoading = false;
      message.error(`${action.payload}`);
    });

    builder.addCase(updateRack.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(updateRack.fulfilled, (state) => {
      state.isLoading = false;
      message.success(`Rack updated Successfully`);
    });
    builder.addCase(updateRack.rejected, (state, action) => {
      state.isLoading = false;
      message.error(`${action.payload}`);
    });
  },
});
export const {
  pageNumberChange,
  pageSizeChange,
  searchBy,
  sortBy,
  clearRacksFilters,
  racksStatusFilter,
} = racksSlice.actions;

export default racksSlice.reducer;
